/**
 * The one set of date formatters, and the one relative-time ladder.
 */
import { dateFormatter } from "./intl.js";
/**
 * A date with no time and no zone (`2026-07-01`, the shape billing dates and
 * `*_on` columns arrive in).
 *
 * `new Date("2026-07-01")` is UTC midnight, so rendering it in local time rolls
 * it back a calendar day anywhere west of UTC — a renewal dated the 1st shows
 * as the 30th in Los Angeles. Pinning the render to UTC keeps the date the one
 * that was written. Full timestamps (`*_at`) are real instants and keep local
 * rendering, which is what a person means by "when did this happen".
 */
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
const SECOND = 1000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
function toDate(value) {
    if (value === null || value === undefined || value === "")
        return null;
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
}
/** `"UTC"` for a date-only string, otherwise the viewer's zone. */
function zoneFor(value) {
    return typeof value === "string" && DATE_ONLY.test(value) ? "UTC" : undefined;
}
function render(value, options) {
    const date = toDate(value);
    if (!date)
        return "";
    return dateFormatter("en-US", { ...options, timeZone: zoneFor(value) }).format(date);
}
/** A date: "Jul 1, 2026". */
export function formatDate(value) {
    return render(value, { year: "numeric", month: "short", day: "numeric" });
}
/** The fields of a date and time, as `formatDateTime` and `formatDateTimeUtc` both write them. */
const DATE_TIME = {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
};
/** A date and time: "Jul 1, 2026, 2:25 PM". */
export function formatDateTime(value) {
    return render(value, DATE_TIME);
}
/**
 * A date and time on the UTC clock, and saying so: "Jul 1, 2026, 2:25 PM UTC".
 *
 * For a surface that counts in UTC days, such as an API usage meter whose
 * buckets are UTC days: a call made at 11:30 PM in Los Angeles is counted on
 * the next UTC day, and printing it on that day is what lets the reader find
 * it in the bucket it was counted in. Everywhere else an instant renders in
 * the viewer's own zone (`formatDateTime`), and the zone name is what keeps
 * this one from passing for local time.
 */
export function formatDateTimeUtc(value) {
    const date = toDate(value);
    if (!date)
        return "";
    const written = dateFormatter("en-US", { ...DATE_TIME, timeZone: "UTC" }).format(date);
    return `${written} UTC`;
}
/** A time of day: "2:25 PM". */
export function formatTime(value) {
    return render(value, { hour: "numeric", minute: "2-digit" });
}
/**
 * How long ago something happened.
 *
 * | elapsed              | output      |
 * | -------------------- | ----------- |
 * | future, or under 5s  | `just now`  |
 * | under 1 minute       | `42s ago`   |
 * | under 1 hour         | `12m ago`   |
 * | under 24 hours       | `3h ago`    |
 * | 24 hours to under 48 | `yesterday` |
 * | under 7 days         | `3d ago`    |
 * | under 30 days        | `2w ago`    |
 * | under 365 days       | `5mo ago`   |
 * | 365 days and over    | `2y ago`    |
 *
 * A future instant reads "just now" rather than counting up: a clock a few
 * seconds ahead of the server is ordinary, and "-4s ago" is not a thing that
 * happened.
 *
 * `now` is a parameter so the ladder is testable and so a caller can drive it
 * from a ticking source — a frontend that wants "2m ago" to advance on screen
 * passes its own heartbeat here rather than this module owning an interval,
 * which is what keeps the package free of a framework and of a timer that
 * outlives the render.
 *
 * A `value` that is missing or unparseable renders as `""`, and so does a `now`
 * that is not a finite instant.
 */
export function formatRelativeTime(value, now = Date.now()) {
    const date = toDate(value);
    if (!date)
        return "";
    const nowMs = now instanceof Date ? now.getTime() : now;
    // A caller driving this from a ticking heartbeat hands over whatever its
    // clock holds, and a momentarily unset one is a `NaN`. Every rung below is a
    // `<` comparison that `NaN` fails, so an unguarded broken clock falls all the
    // way through the ladder and prints "NaNy ago" in the cell. A clock that is
    // not a time is a missing value, and a missing value renders as nothing.
    if (!Number.isFinite(nowMs))
        return "";
    const elapsed = nowMs - date.getTime();
    if (elapsed < 5 * SECOND)
        return "just now";
    const seconds = Math.floor(elapsed / SECOND);
    if (seconds < 60)
        return `${seconds}s ago`;
    const minutes = Math.floor(elapsed / MINUTE);
    if (minutes < 60)
        return `${minutes}m ago`;
    const hours = Math.floor(elapsed / HOUR);
    if (hours < 24)
        return `${hours}h ago`;
    const days = Math.floor(elapsed / DAY);
    if (days === 1)
        return "yesterday";
    if (days < 7)
        return `${days}d ago`;
    if (days < 30)
        return `${Math.floor(days / 7)}w ago`;
    if (days < 365)
        return `${Math.floor(days / 30)}mo ago`;
    return `${Math.floor(days / 365)}y ago`;
}
/**
 * A date rendered to a caller's pattern.
 *
 * WHY THIS EXISTS WHEN THE THREE FIXED SHAPES ARE THE POINT.
 * `formatDate`, `formatDateTime` and `formatTime` are the answer to "how does
 * Privacy Bee print a date", and a surface reaching past them for
 * `"MMM D, YYYY"` is asking the same question and getting a different answer.
 * Those three stay the default and a review should ask why a pattern is not one
 * of them.
 *
 * What the pattern is for is the copy a date is set INSIDE: a sentence
 * ("11:12am on November 3, 2024"), a label ("June 17th, 2025 2:25pm"), a
 * machine-shaped column ("2026-07-01"). Those are not a fourth house style —
 * they are a caller's sentence with a date in the middle of it — and the
 * alternative to publishing them here is every app keeping a date library to
 * write them, which is the duplication this package exists to end.
 *
 * ZONE AND BLANKS FOLLOW THE REST OF THE MODULE.
 * A date-only string renders in UTC, a full timestamp in the viewer's zone, and
 * a missing or unparseable value renders as `""`. The pattern never changes
 * which of those applies.
 *
 * THE TOKENS ARE MOMENT'S, AND THE LIST IS CLOSED.
 * Only the tokens below are recognised. Anything else in the pattern is
 * literal, which is what makes `"5/12/25"`'s slashes and `"2026-07-01"`'s
 * dashes work without escaping. Text that would collide with a token is
 * bracketed, as in `"h:mma [on] MMMM D, YYYY"` — `[on]` prints `on` rather than
 * an ordinal and a nonce.
 *
 * | token  | output                  |
 * | ------ | ----------------------- |
 * | `YYYY` | `2026`                  |
 * | `YY`   | `26`                    |
 * | `MMMM` | `July`                  |
 * | `MMM`  | `Jul`                   |
 * | `MM`   | `07`                    |
 * | `M`    | `7`                     |
 * | `DD`   | `01`                    |
 * | `D`    | `1`                     |
 * | `Do`   | `1st`                   |
 * | `dddd` | `Wednesday`             |
 * | `ddd`  | `Wed`                   |
 * | `HH`   | `14`                    |
 * | `H`    | `14`                    |
 * | `hh`   | `02`                    |
 * | `h`    | `2`                     |
 * | `mm`   | `25`                    |
 * | `m`    | `25`                    |
 * | `ss`   | `09`                    |
 * | `s`    | `9`                     |
 * | `A`    | `PM`                    |
 * | `a`    | `pm`                    |
 */
export function formatDatePattern(value, pattern) {
    const date = toDate(value);
    if (!date)
        return "";
    const fields = fieldsFor(date, zoneFor(value));
    // One pass, longest token first at each position, so `MMMM` is never read as
    // `MMM` plus a stray `M`. A bracketed run is copied out whole; an unclosed
    // `[` is the rest of the string, which is the reading that loses no
    // characters.
    let out = "";
    let i = 0;
    while (i < pattern.length) {
        if (pattern[i] === "[") {
            const close = pattern.indexOf("]", i + 1);
            if (close === -1)
                return out + pattern.slice(i + 1);
            out += pattern.slice(i + 1, close);
            i = close + 1;
            continue;
        }
        const token = TOKENS.find((t) => pattern.startsWith(t, i));
        if (token) {
            out += fields[token];
            i += token.length;
            continue;
        }
        out += pattern[i];
        i += 1;
    }
    return out;
}
/**
 * Longest first: `find` takes the first match, so this order IS the
 * longest-token-wins rule above.
 */
const TOKENS = ["YYYY", "MMMM", "dddd", "MMM", "ddd", "YY", "MM", "DD", "Do", "HH", "hh", "mm", "ss", "M", "D", "H", "h", "m", "s", "A", "a"];
/**
 * The calendar fields of `date` AS SEEN IN `zone`.
 *
 * Read through `Intl` rather than off the `Date`: `getHours()` answers in the
 * host's zone and nothing else, so a date-only string that must render in UTC
 * has no way to ask. `formatToParts` is the one API that will state the hour,
 * the day and the month in a named zone, and `hourCycle: "h23"` is what stops
 * midnight coming back as `24`.
 *
 * Month and weekday names come from the same `en-US` this module's other
 * formatters use. Nothing here is locale-aware on purpose: a pattern is a
 * caller's literal string with English separators already in it, so resolving
 * half of it against the viewer's locale would produce a sentence in two
 * languages.
 */
function fieldsFor(date, timeZone) {
    const parts = new Map(dateFormatter("en-US", {
        timeZone,
        hourCycle: "h23",
        year: "numeric",
        month: "long",
        day: "2-digit",
        weekday: "long",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
    })
        .formatToParts(date)
        .map((part) => [part.type, part.value]));
    const year = parts.get("year") ?? "";
    const monthName = parts.get("month") ?? "";
    const weekday = parts.get("weekday") ?? "";
    const day = Number(parts.get("day"));
    const hour24 = Number(parts.get("hour"));
    const minute = parts.get("minute") ?? "";
    const second = parts.get("second") ?? "";
    const monthIndex = MONTHS.indexOf(monthName);
    const month = monthIndex + 1;
    const hour12 = hour24 % 12 || 12;
    return {
        YYYY: year,
        YY: year.slice(-2),
        MMMM: monthName,
        MMM: monthName.slice(0, 3),
        MM: pad(month),
        M: String(month),
        DD: pad(day),
        D: String(day),
        Do: ordinal(day),
        dddd: weekday,
        ddd: weekday.slice(0, 3),
        // `H` and `HH` differ in moment and the sources here are zero-padded, so
        // `H` strips what `hourCycle: "h23"` added rather than adding to it.
        HH: pad(hour24),
        H: String(hour24),
        hh: pad(hour12),
        h: String(hour12),
        mm: minute,
        m: String(Number(minute)),
        ss: second,
        s: String(Number(second)),
        A: hour24 < 12 ? "AM" : "PM",
        a: hour24 < 12 ? "am" : "pm",
    };
}
const MONTHS = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
];
function pad(value) {
    return String(value).padStart(2, "0");
}
/**
 * 1st, 2nd, 3rd, 4th — and 11th, 12th, 13th, which are the reason the tens are
 * checked before the units.
 */
function ordinal(day) {
    if (day % 100 >= 11 && day % 100 <= 13)
        return `${day}th`;
    if (day % 10 === 1)
        return `${day}st`;
    if (day % 10 === 2)
        return `${day}nd`;
    if (day % 10 === 3)
        return `${day}rd`;
    return `${day}th`;
}
